// src/components/resources/quizzes/components/question-types/TextQuestion.tsx

'use client'

import { useState } from 'react'
import { cn } from '~/lib/utils'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import { Button } from '~/components/ui/Button'
import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

interface TextQuestionProps {
  question: QuizQuestion
  selectedAnswer: string | string[]
  onAnswerSelect: (answer: string) => void
  showFeedback: boolean
  isAnswerCorrect?: boolean  // Keep for type compatibility with QuestionRenderer
  disabled?: boolean
}

export function TextQuestion({
  question,
  selectedAnswer,
  onAnswerSelect,
  showFeedback,
  isAnswerCorrect,
  disabled
}: TextQuestionProps): JSX.Element {
  const [isFocused, setIsFocused] = useState(false)
  const [draftAnswer, setDraftAnswer] = useState(selectedAnswer as string ?? '')
  const answer = selectedAnswer as string ?? ''

  // Use the isAnswerCorrect prop from parent which already handles case-insensitive comparison
  const isCorrect = showFeedback ? isAnswerCorrect : false

  const handleSubmit = (): void => {
    if (typeof draftAnswer === 'string' && draftAnswer.trim()) {
      onAnswerSelect(draftAnswer.trim().toLowerCase()) // Normalize case when submitting
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    if (typeof value === 'string') {
      setDraftAnswer(value)
    }
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={draftAnswer}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={question.placeholder ?? 'Type your answer here...'}
          className={cn(
            "w-full rounded-lg border bg-white px-4 py-3 text-base transition-all dark:bg-gray-800",
            disabled
              ? "cursor-not-allowed opacity-50"
              : "focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400",
            isFocused
              ? "border-indigo-500 ring-2 ring-indigo-500 dark:border-indigo-400 dark:ring-indigo-400"
              : "border-gray-300 dark:border-gray-700",
            showFeedback && answer && (isCorrect
              ? "border-green-500 ring-2 ring-green-500 dark:border-green-400 dark:ring-green-400"
              : "border-red-500 ring-2 ring-red-500 dark:border-red-400 dark:ring-red-400")
          )}
        />

        {/* Feedback Icon */}
        {showFeedback && answer && !question.explanation && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isCorrect ? (
              <RiCheckLine className="h-5 w-5 text-green-500 dark:text-green-400" />
            ) : (
              <RiCloseLine className="h-5 w-5 text-red-500 dark:text-red-400" />
            )}
          </div>
        )}
      </div>

      {/* Submit Button */}
      {!showFeedback && draftAnswer && draftAnswer !== answer && (
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            className="w-[120px]"
          >
            Submit
          </Button>
        </div>
      )}

      {/* Feedback Message - Only show if there's no explanation */}
      {showFeedback && answer && !question.explanation && (
        <div
          className={cn(
            "rounded-lg p-4",
            isCorrect
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
          )}
        >
          <p className="text-sm">
            {isCorrect
              ? "Correct!"
              : "Incorrect. Please try again."}
          </p>
        </div>
      )}
    </div>
  )
} 